import { useState } from 'react';
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, FileText, Users, CheckCircle2, Clock, Calendar, ArrowRight, X } from "lucide-react";
import type { LegalDoc, EligibilityGroup, ProcessStep, AnnouncementItem, ProgressPhase } from "@/lib/index";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};

interface LegalDocCardProps {
  doc: LegalDoc;
}

export function LegalDocCard({ doc }: LegalDocCardProps) {
  return (
    <motion.div {...fadeInUp}>
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-lg mb-2">{doc.name}</CardTitle>
              <CardDescription className="text-sm">
                Số: {doc.documentNumber}
              </CardDescription>
            </div>
            <Badge variant={doc.status === "Đã ban hành" ? "default" : "secondary"}>
              {doc.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{doc.date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>{doc.issuingAuthority}</span>
          </div>
        </CardContent>
        {doc.downloadUrl && (
          <CardFooter>
            <Button variant="outline" className="w-full" asChild>
              <a href={doc.downloadUrl} download>
                <Download className="h-4 w-4 mr-2" />
                Tải xuống
              </a>
            </Button>
          </CardFooter>
        )}
      </Card>
    </motion.div>
  );
}

interface EligibilityGroupCardProps {
  group: EligibilityGroup;
}

export function EligibilityGroupCard({ group }: EligibilityGroupCardProps) {
  return (
    <motion.div {...fadeInUp}>
      <Card className={`h-full hover:shadow-lg transition-shadow ${
        group.isMainGroup ? "border-primary bg-accent/10" : ""
      }`}>
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono">
                  Nhóm {group.groupNumber}
                </Badge>
                {group.isMainGroup && (
                  <Badge variant="default">Ưu tiên</Badge>
                )}
              </div>
              <CardTitle className="text-lg">{group.name}</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {group.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface ProcessStepCardProps {
  step: ProcessStep;
}

export function ProcessStepCard({ step }: ProcessStepCardProps) {
  return (
    <motion.div {...fadeInUp}>
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
              {step.stepNumber}
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg mb-2">{step.title}</CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{step.duration}</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface AnnouncementCardProps {
  announcement: AnnouncementItem;
}

export function AnnouncementCard({ announcement }: AnnouncementCardProps) {
  const [open, setOpen] = useState(false);

  const categoryColors: Record<AnnouncementItem["category"], string> = {
    "Thông báo dự án": "default",
    "Thông tin dự án": "default",
    "Giá bán": "secondary",
    "Tiến độ": "outline",
    "Hồ sơ": "outline",
  };

  const contentLines = announcement.content?.split('\n') ?? [];

  return (
    <>
      <motion.div {...fadeInUp}>
        <Card className="h-full hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={categoryColors[announcement.category] as any}>
                {announcement.category}
              </Badge>
              <span className="text-sm text-muted-foreground">{announcement.date}</span>
            </div>
            <CardTitle className="text-lg">{announcement.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {announcement.summary}
            </p>
          </CardContent>
          <CardFooter>
            <Button
              variant="ghost"
              className="w-full justify-between"
              onClick={() => setOpen(true)}
            >
              Xem chi tiết
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant={categoryColors[announcement.category] as any}>
                {announcement.category}
              </Badge>
              <span className="text-sm text-muted-foreground">{announcement.date}</span>
            </div>
            <DialogTitle className="text-xl leading-snug">{announcement.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm font-medium text-muted-foreground mb-1">Tóm tắt</p>
              <p className="text-sm leading-relaxed">{announcement.summary}</p>
            </div>
            {contentLines.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Chi tiết</p>
                <ul className="space-y-2">
                  {contentLines.map((line, i) => (
                    <li key={i} className="text-sm leading-relaxed flex gap-2">
                      {line.startsWith('•') || line.match(/^\d\./) ? (
                        <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
                      ) : null}
                      <span className={line.startsWith('•') || line.match(/^\d\./) ? '' : 'font-medium'}>
                        {line.replace(/^[•\d\.\s]+/, '')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ProgressPhaseCardProps {
  phase: ProgressPhase;
}

export function ProgressPhaseCard({ phase }: ProgressPhaseCardProps) {
  const statusConfig = {
    "Hoàn thành": { variant: "default" as const, icon: CheckCircle2, color: "text-primary" },
    "Đang thi công": { variant: "secondary" as const, icon: Clock, color: "text-orange-500" },
    "Chưa bắt đầu": { variant: "outline" as const, icon: Calendar, color: "text-muted-foreground" }
  };

  const config = statusConfig[phase.status];
  const StatusIcon = config.icon;

  return (
    <motion.div {...fadeInUp}>
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono">
                  Giai đoạn {phase.phaseNumber}
                </Badge>
                <Badge variant={config.variant}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {phase.status}
                </Badge>
              </div>
              <CardTitle className="text-lg">{phase.name}</CardTitle>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold font-mono ${config.color}`}>
                {phase.completionPercentage}%
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={phase.completionPercentage} className="h-2" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            {phase.description}
          </p>
          <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{phase.startDate}</span>
            </div>
            <span>→</span>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{phase.endDate}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}